import React, { useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { PageHeader, StatCard, Section } from "../components/shared/Primitives";
import EntryModal from "../components/shared/EntryModal";
import CategoryManager from "../components/shared/CategoryManager";
import { Plus, Settings, Trash2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_PLATFORMS = [
  { key: "LinkedIn", color: "#0067B8" },
  { key: "Facebook", color: "#1877F2" },
  { key: "Other", color: "#707070" },
];

export default function Marketing() {
  const [articles, setArticles] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [modal, setModal] = useState({ open: false, entry: null });
  const [platMgrOpen, setPlatMgrOpen] = useState(false);
  const seededRef = useRef(false);

  const load = async () => {
    const { data } = await api.get("/marketing/articles");
    setArticles(data.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || "")));
  };

  const loadPlatforms = async () => {
    let { data } = await api.get("/categories/marketing_platforms");
    if ((!data || data.length === 0) && !seededRef.current) {
      seededRef.current = true;
      await Promise.all(
        DEFAULT_PLATFORMS.map((p, i) =>
          api.post("/categories/marketing_platforms", { name: p.key, color: p.color, order: i, hidden: false })
        )
      );
      const r = await api.get("/categories/marketing_platforms");
      data = r.data;
    }
    const seen = new Set();
    const deduped = [];
    for (const c of data) {
      if (seen.has(c.name)) continue;
      seen.add(c.name);
      deduped.push(c);
    }
    deduped.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
    setPlatforms(deduped);
  };

  useEffect(() => {
    load();
    loadPlatforms();
  }, []);

  const save = async (p) => {
    if (p.id) await api.put(`/marketing/articles/${p.id}`, p);
    else await api.post(`/marketing/articles`, p);
    toast.success("Article saved");
    load();
  };
  const del = async () => {
    if (modal.entry?.id) {
      await api.delete(`/marketing/articles/${modal.entry.id}`);
      toast.success("Article deleted");
      load();
    }
  };

  const platColor = (name) => platforms.find((p) => p.name === name)?.color || "#0F1115";

  return (
    <div>
      <PageHeader
        eyebrow="Outreach"
        title="Marketing"
        description="Track articles you've published and where they ran."
        right={
          <>
            <Button variant="outline" onClick={() => setPlatMgrOpen(true)} data-testid="marketing-platforms-btn">
              <Settings size={14} className="mr-2" /> Platforms
            </Button>
            <Button
              onClick={() => setModal({ open: true, entry: null })}
              className="bg-[var(--rw-sidebar)] hover:bg-[var(--rw-sidebar-hover)]"
              data-testid="add-article-btn"
            >
              <Plus size={14} className="mr-2" /> New Article
            </Button>
          </>
        }
      />
      <div className="p-8 space-y-5">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard label="Articles" value={articles.length} testid="marketing-stat-total" />
          <StatCard
            label="LinkedIn posts"
            value={articles.filter((a) => (a.platforms || []).includes("LinkedIn") || a.posted_to === "LinkedIn").length}
          />
          <StatCard
            label="Facebook posts"
            value={articles.filter((a) => (a.platforms || []).includes("Facebook") || a.posted_to === "Facebook").length}
          />
          <StatCard
            label="Other"
            value={articles.filter((a) => (a.platforms || []).includes("Other") || a.posted_to === "Other").length}
          />
        </div>

        <Section title="Articles" color="#0F1115" count={articles.length}>
          <table className="w-full text-sm">
            <thead className="bg-[var(--rw-surface-2)]">
              <tr className="text-left text-[10px] uppercase tracking-[0.08em] font-semibold text-neutral-500">
                <th className="py-2 px-3">Title</th>
                <th className="py-2 px-3">Summary</th>
                <th className="py-2 px-3">Posted To</th>
                <th className="py-2 px-3">URL</th>
                <th className="py-2 px-3"></th>
              </tr>
            </thead>
            <tbody>
              {articles.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center text-neutral-400">No articles yet. Click New Article to add one.</td></tr>
              )}
              {articles.map((a) => (
                <tr
                  key={a.id}
                  className="border-t border-[var(--rw-border)] hover:bg-[var(--rw-surface-2)] cursor-pointer"
                  onClick={() => setModal({ open: true, entry: a })}
                  data-testid={`article-row-${a.id}`}
                >
                  <td className="py-2 px-3 font-semibold align-top">{a.title}</td>
                  <td className="py-2 px-3 text-neutral-600 align-top max-w-md">
                    <div className="line-clamp-2 text-xs">{a.summary}</div>
                  </td>
                  <td className="py-2 px-3 align-top">
                    <div className="flex flex-wrap gap-1">
                      {(a.platforms || (a.posted_to ? [a.posted_to] : [])).map((p) => (
                        <span
                          key={p}
                          className="text-[10px] uppercase tracking-widest text-white px-2 py-0.5 rounded"
                          style={{ background: platColor(p) }}
                        >
                          {p}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-2 px-3 align-top">
                    {a.url ? (
                      <a
                        href={a.url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-xs underline text-blue-700 font-mono-rw inline-flex items-center gap-1"
                      >
                        <ExternalLink size={11} /> open
                      </a>
                    ) : "—"}
                  </td>
                  <td className="py-2 px-3 text-right">
                    <button
                      onClick={async (e) => {
                        e.stopPropagation();
                        if (!window.confirm("Delete this article?")) return;
                        await api.delete(`/marketing/articles/${a.id}`);
                        toast.success("Article deleted");
                        load();
                      }}
                      className="text-red-400 hover:text-red-700"
                      data-testid={`article-delete-${a.id}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      </div>

      <EntryModal
        open={modal.open}
        onOpenChange={(v) => setModal({ ...modal, open: v })}
        title={modal.entry ? "Edit Article" : "New Article"}
        entry={modal.entry}
        promptAddContact={false}
        fields={[
          { name: "title", label: "Title", full: true },
          { name: "url", label: "Article URL", full: true },
          { name: "summary", label: "Brief Summary", type: "textarea", full: true },
          {
            name: "platforms",
            label: "Posted To",
            type: "multiselect",
            options: platforms.map((p) => p.name),
            full: true,
          },
        ]}
        onSave={save}
        onDelete={del}
      />

      <CategoryManager
        open={platMgrOpen}
        onOpenChange={setPlatMgrOpen}
        kind="marketing_platforms"
        onChanged={loadPlatforms}
      />
    </div>
  );
}
